// ============================================================================
// Integration Hooks — TanStack Query
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTenant } from '@/contexts'
import {
  fetchProviders,
  fetchCredentialSets,
  fetchCredentials,
  fetchIntegrationHealth,
  fetchIntegrationOverview,
  fetchSyncLog,
  testCredentials,
  createCredentialSet,
  mapLocation,
  triggerInitialSync,
  rotateCredentials,
  triggerManualSync,
  updateCredential,
  deleteCredential,
  updateCredentialSet,
  bulkUpdateCredentials,
  removeIntegration,
} from '@/lib/api/integrations'
import type { CredentialModel } from '@/types/integrations'

// -- Query Keys ----------------------------------------------------------------

export const integrationKeys = {
  all: ['integrations'] as const,
  providers: () => [...integrationKeys.all, 'providers'] as const,
  credentialSets: (organizationId: string) => [...integrationKeys.all, 'credential-sets', organizationId] as const,
  credentials: (organizationId: string) => [...integrationKeys.all, 'credentials', organizationId] as const,
  health: (organizationId: string) => [...integrationKeys.all, 'health', organizationId] as const,
  overview: (organizationId: string) => [...integrationKeys.all, 'overview', organizationId] as const,
  syncLog: (organizationId: string, filters?: Record<string, unknown>) =>
    [...integrationKeys.all, 'sync-log', organizationId, filters] as const,
}

// -- Read Hooks ----------------------------------------------------------------

export function useIntegrationProviders() {
  return useQuery({
    queryKey: integrationKeys.providers(),
    queryFn: fetchProviders,
    staleTime: 1000 * 60 * 30, // 30 min — providers rarely change
  })
}

export function useCredentialSets() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: integrationKeys.credentialSets(organizationId || ''),
    queryFn: () => fetchCredentialSets(organizationId!),
    enabled: !!organizationId,
  })
}

export function useIntegrationCredentials() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: integrationKeys.credentials(organizationId || ''),
    queryFn: () => fetchCredentials(organizationId!),
    enabled: !!organizationId,
  })
}

export function useIntegrationHealth() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: integrationKeys.health(organizationId || ''),
    queryFn: () => fetchIntegrationHealth(organizationId!),
    enabled: !!organizationId,
    refetchInterval: 30000, // Auto-refresh every 30s
  })
}

export function useSyncLog(filters?: { credentialId?: string; credentialIds?: string[]; result?: string }) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: integrationKeys.syncLog(organizationId || '', filters),
    queryFn: () => fetchSyncLog(organizationId!, filters),
    enabled: !!organizationId,
  })
}

export function useIntegrationOverview() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: integrationKeys.overview(organizationId || ''),
    queryFn: () => fetchIntegrationOverview(organizationId!),
    enabled: !!organizationId,
    refetchInterval: 30000,
  })
}

// -- Mutation Hooks ------------------------------------------------------------

export function useTestCredentials() {
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ provider, username, password }: {
      provider: string; username: string; password: string
    }) => testCredentials(currentOrganization!.id, provider, username, password),
  })
}

export function useCreateCredentialSet() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ provider, label, username, password }: {
      provider: string; label: string; username: string; password: string
    }) => createCredentialSet(currentOrganization!.id, provider, label, username, password),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentialSets(organizationId) })
      }
      toast.success('Credentials saved successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to save credentials', { description: error.message })
    },
  })
}

export function useMapLocation() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ credentialSetId, locationId, providerConfig, syncConfig }: {
      credentialSetId: string
      locationId: string
      providerConfig?: Record<string, unknown>
      syncConfig?: Record<string, unknown>
    }) => mapLocation(currentOrganization!.id, credentialSetId, locationId, providerConfig, syncConfig),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentials(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
      }
      toast.success('Location mapped to integration')
    },
    onError: (error: Error) => {
      toast.error('Failed to map location', { description: error.message })
    },
  })
}

export function useTriggerSync() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (credentialId: string) => triggerManualSync(credentialId),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.syncLog(organizationId) })
      }
      toast.success('Sync queued — will run within 60 seconds')
    },
    onError: (error: Error) => {
      toast.error('Sync failed', { description: error.message })
    },
  })
}

export function useInitialSync() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (credentialId: string) => triggerInitialSync(currentOrganization!.id, credentialId),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.syncLog(organizationId) })
      }
      toast.success('Initial sync queued — will run within 60 seconds')
    },
    onError: (error: Error) => {
      toast.error('Initial sync failed', { description: error.message })
    },
  })
}

export function useRotateCredentials() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ credentialSetId, newPassword }: {
      credentialSetId: string; newPassword: string
    }) => rotateCredentials(currentOrganization!.id, credentialSetId, newPassword),
    onSuccess: (result) => {
      if (result.success) {
        const organizationId = currentOrganization?.id
        if (organizationId) {
          queryClient.invalidateQueries({ queryKey: integrationKeys.credentialSets(organizationId) })
        }
        toast.success('Credentials rotated successfully')
      } else {
        toast.error('Rotation failed', { description: result.message })
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to rotate credentials', { description: error.message })
    },
  })
}

export function useUpdateCredential() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ id, updates }: {
      id: string
      updates: Parameters<typeof updateCredential>[1]
    }) => updateCredential(id, updates),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentials(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
      }
      toast.success('Integration updated')
    },
    onError: (error: Error) => {
      toast.error('Failed to update integration', { description: error.message })
    },
  })
}

export function useDeleteCredential() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (id: string) => deleteCredential(id),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentials(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
      }
      toast.success('Integration connection removed')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove connection', { description: error.message })
    },
  })
}

export function useUpdateCredentialSet() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await updateCredentialSet(id, { is_active: isActive })
      await bulkUpdateCredentials(id, { is_active: isActive })
    },
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.overview(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentials(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentialSets(organizationId) })
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to update integration', { description: error.message })
    },
  })
}

export function useRemoveIntegration() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ credentialSetId, credentialModel }: {
      credentialSetId: string
      credentialModel: CredentialModel
    }) => removeIntegration(credentialSetId, credentialModel),
    onSuccess: () => {
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.overview(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentials(organizationId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentialSets(organizationId) })
      }
      toast.success('Integration removed')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove integration', { description: error.message })
    },
  })
}
